import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

// Fetch projects with pagination
export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (params, { rejectWithValue }) => {
    try {
      const { 
        page = 1, 
        limit = 10, 
        sortField, 
        sortOrder, 
        search,
        projectStatus,
        projectManagerId 
      } = params;
      
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...(sortField && { sortField }),
        ...(sortOrder && { sortOrder }),
        ...(search && { search }),
        ...(projectStatus && { projectStatus }),
        ...(projectManagerId && { projectManagerId }),
        ...(params.startDate && { startDate: params.startDate }),
        ...(params.endDate && { endDate: params.endDate })
      }).toString();

      const response = await fetch(`${config.apiUrl}/api/projects?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch projects');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Load column preferences
export const loadProjectColumnPreferences = createAsyncThunk(
  'projects/loadColumnPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/preferences?type=projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to load preferences');
      }

      return data.preferences?.projectColumns || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Save column preferences
export const saveProjectColumnPreferences = createAsyncThunk(
  'projects/saveColumnPreferences',
  async (columns, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/preferences/columns`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          columns: Array.isArray(columns) ? columns : [],
          type: 'projects'
        })
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to save preferences');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action creator for deleting projects
export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete project');
      }

      return { projectId, message: data.message };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this export with the other action creators
export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update project');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these action creators
export const uploadProjectAttachments = createAsyncThunk(
  'projects/uploadAttachments',
  async ({ projectId, files }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to upload attachments');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const deleteProjectAttachment = createAsyncThunk(
  'projects/deleteAttachment',
  async ({ projectId, attachmentId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete attachment');
      }

      return { projectId, attachmentId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this action creator
export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch project');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action creators for archive/unarchive
export const archiveProject = createAsyncThunk(
  'projects/archiveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to archive project');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const unarchiveProject = createAsyncThunk(
  'projects/unarchiveProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to unarchive project');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this new action creator
export const fetchProjectDocuments = createAsyncThunk(
  'projects/fetchDocuments',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch documents');
      }

      return data.documents;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState: {
    projects: [],
    currentProject: null,
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0
    },
    columnPreferences: []
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.projects;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(loadProjectColumnPreferences.fulfilled, (state, action) => {
        state.columnPreferences = action.payload.projectColumns || [];
      })
      .addCase(saveProjectColumnPreferences.fulfilled, (state, action) => {
        state.columnPreferences = action.payload.columns || [];
        showToast('Column preferences saved successfully', 'success');
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter(project => project._id !== action.payload.projectId);
        showToast('Project deleted successfully', 'success');
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.map(project => 
          project._id === action.payload.project._id ? action.payload.project : project
        );
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(uploadProjectAttachments.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentProject && state.currentProject._id === action.payload.projectId) {
          state.currentProject.attachments = action.payload.attachments;
        }
        showToast('Files uploaded successfully', 'success');
      })
      .addCase(uploadProjectAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload || 'Failed to upload files', 'error');
      })
      .addCase(deleteProjectAttachment.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentProject && state.currentProject._id === action.payload.projectId) {
          state.currentProject.attachments = state.currentProject.attachments.filter(
            att => att._id !== action.payload.attachmentId
          );
        }
        showToast('Attachment deleted successfully', 'success');
      })
      .addCase(deleteProjectAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload || 'Failed to delete attachment', 'error');
      })
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProject = action.payload.project;
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(archiveProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.map(project =>
          project._id === action.payload.project._id ? action.payload.project : project
        );
        if (state.currentProject?._id === action.payload.project._id) {
          state.currentProject = action.payload.project;
        }
        showToast('Project archived successfully', 'success');
      })
      .addCase(archiveProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(unarchiveProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unarchiveProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.map(project =>
          project._id === action.payload.project._id ? action.payload.project : project
        );
        if (state.currentProject?._id === action.payload.project._id) {
          state.currentProject = action.payload.project;
        }
        showToast('Project unarchived successfully', 'success');
      })
      .addCase(unarchiveProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchProjectDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProject = {
          ...state.currentProject,
          documents: action.payload
        };
      })
      .addCase(fetchProjectDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentProject } = projectsSlice.actions;
export default projectsSlice.reducer; 